/**
 * Admin Author Management Console
 * 
 * GOVERNANCE & OBSERVABILITY SURFACE
 * 
 * PURPOSE: Administrative oversight for author accounts, content quality,
 * and performance signals - NOT a payout engine or creative tool.
 * 
 * DESIGN PRINCIPLES (Non-Negotiable):
 * 1. Admin-Only, Read-Only (Pre-MVP)
 * 2. Observability Before Monetization
 * 3. Economic Neutrality - Display engagement, NOT earnings
 * 4. Role-Aligned Semantics - Authors are content contributors
 * 
 * PANELS:
 * A. Author Accounts - Identity, access, status management
 * B. Content Portfolio - Titles by author with drill-down
 * C. Performance Tracking - Engagement signals (explicitly non-financial)
 * D. Author Recognition - Badges and tiers (informational only)
 * 
 * EXPLICITLY EXCLUDED:
 * - Author earnings / Revenue share percentages
 * - Payout schedules / Tax documents
 * - Author self-service actions
 * - Contract management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  UserCog,
  BookOpen,
  TrendingUp,
  TrendingDown,
  Award,
  Users,
  Clock,
  Search,
  RefreshCw,
  Eye,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Star,
  Minus
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toLocaleString() || '0';
};

const formatMinutes = (minutes) => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  }
  return `${Math.round(minutes)}m`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getTierColor = (tier) => {
  switch (tier) {
    case 'featured': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50';
    case 'established': return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
    default: return 'bg-slate-500/20 text-slate-300 border-slate-500/50';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'active': return 'bg-green-500/20 text-green-300 border-green-500/50';
    case 'suspended': return 'bg-red-500/20 text-red-300 border-red-500/50';
    case 'pending': return 'bg-amber-500/20 text-amber-300 border-amber-500/50';
    default: return 'bg-slate-500/20 text-slate-300 border-slate-500/50';
  }
};

const getTrendIcon = (direction) => {
  switch (direction) {
    case 'up': return <TrendingUp className="w-4 h-4 text-green-400" />;
    case 'down': return <TrendingDown className="w-4 h-4 text-red-400" />;
    default: return <Minus className="w-4 h-4 text-slate-400" />;
  }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Context Banner - Non-Financial Disclaimer
const ContextBanner = () => (
  <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4 mb-6">
    <div className="flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-sm text-blue-200">
          <strong>Engagement & Observability Console</strong>
        </p>
        <p className="text-xs text-blue-300/80 mt-1">
          This console provides engagement and content performance visibility only.
          Author payouts and revenue attribution are not enabled in this environment.
        </p>
      </div>
    </div>
  </div>
);

// Author Detail Modal
const AuthorDetailModal = ({ author, isOpen, onClose, onViewTitles }) => {
  if (!author) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-3">
            {author.headshot_url ? (
              <img 
                src={author.headshot_url} 
                alt={author.pen_name || 'Author'} 
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center">
                <UserCog className="w-6 h-6 text-slate-400" />
              </div>
            )}
            <div>
              <span>{author.pen_name || `${author.first_name} ${author.last_name}` || 'Unknown Author'}</span>
              <Badge className={`ml-2 ${getTierColor(author.tier)}`}>
                {author.tier?.charAt(0).toUpperCase() + author.tier?.slice(1)}
              </Badge>
            </div>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Author Profile Details (Read-Only)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Status & Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-xs text-slate-400 mb-1">Status</p>
              <Badge className={getStatusColor(author.status)}>
                {author.status?.toUpperCase()}
              </Badge>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-xs text-slate-400 mb-1">Total Titles</p>
              <p className="text-2xl font-bold text-white">{author.title_count}</p>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-300 mb-3">Contact Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-slate-500">Email</p>
                <p className="text-slate-200">{author.contact_email || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-slate-500">Website</p>
                <p className="text-slate-200">
                  {author.website_url ? (
                    <a href={author.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      {author.website_url}
                    </a>
                  ) : 'Not provided'}
                </p>
              </div>
            </div>
          </div>

          {/* Bio */}
          {author.bio && (
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-2">Bio</h4>
              <p className="text-sm text-slate-300">{author.bio}</p>
            </div>
          )}

          {/* Badges */}
          {author.badges && author.badges.length > 0 && (
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Recognition Badges</h4>
              <p className="text-xs text-slate-500 mb-2">Recognition only - not contractual</p>
              <div className="flex flex-wrap gap-2">
                {author.badges.map((badge, idx) => (
                  <Badge key={idx} className="bg-purple-500/20 text-purple-300 border-purple-500/50">
                    <Award className="w-3 h-3 mr-1" />
                    {badge}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Created</p>
              <p className="text-slate-300">{formatDate(author.created_at)}</p>
            </div>
            <div>
              <p className="text-slate-500">Last Activity</p>
              <p className="text-slate-300">{formatDate(author.last_activity_at)}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={() => onViewTitles(author.author_id)}>
              <BookOpen className="w-4 h-4 mr-2" />
              View Titles
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Title Detail Drawer
const TitleDetailDrawer = ({ title, isOpen, onClose }) => {
  if (!title) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-lg text-white">{title.title_name}</DialogTitle>
          <DialogDescription className="text-slate-400">
            by {title.author_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Status</span>
            <Badge className={getStatusColor(title.status)}>
              {title.status?.toUpperCase()}
            </Badge>
          </div>

          {/* Engagement Metrics - Non-Financial */}
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
            <p className="text-xs text-amber-400 mb-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Engagement / Consumption (Non-Payout)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-slate-500">Total Listens</p>
                <p className="text-lg font-bold text-white">{formatNumber(title.total_listens)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Minutes Consumed</p>
                <p className="text-lg font-bold text-white">{formatMinutes(title.total_minutes)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Completion Rate</p>
                <p className="text-lg font-bold text-white">{title.completion_rate}%</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Unique Readers</p>
                <p className="text-lg font-bold text-white">{formatNumber(title.unique_readers)}</p>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Date Added</p>
              <p className="text-slate-300">{formatDate(title.date_added)}</p>
            </div>
            <div>
              <p className="text-slate-500">Last Updated</p>
              <p className="text-slate-300">{formatDate(title.last_updated)}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminAuthorsPage = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [loading, setLoading] = useState(true);
  const [authors, setAuthors] = useState([]);
  const [totalAuthors, setTotalAuthors] = useState(0);
  const [engagementSummary, setEngagementSummary] = useState(null);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [authorTitles, setAuthorTitles] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);
  
  // Modal states
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [showTitlesPanel, setShowTitlesPanel] = useState(false);
  const [showTitleDrawer, setShowTitleDrawer] = useState(false);
  const [loadingTitles, setLoadingTitles] = useState(false);
  
  // Filters from URL params
  const page = parseInt(searchParams.get('page') || '1');
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const days = parseInt(searchParams.get('days') || '30');

  // Update URL params
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Fetch authors list
  const fetchAuthors = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        ...(search && { search }),
        ...(status && { status })
      });

      const response = await fetch(`${API_URL}/api/admin/authors?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch authors');
      
      const data = await response.json();
      setAuthors(data.authors || []);
      setTotalAuthors(data.total || 0);
    } catch (error) {
      console.error('Error fetching authors:', error);
      toast.error('Failed to load authors');
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, search, status]);

  // Fetch engagement summary
  const fetchEngagementSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/authors/summary/engagement?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch engagement summary');
      
      const data = await response.json();
      setEngagementSummary(data);
    } catch (error) {
      console.error('Error fetching engagement summary:', error);
    }
  }, [token, days]);

  // Fetch author detail
  const fetchAuthorDetail = async (authorId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/authors/${authorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch author');
      
      const data = await response.json();
      setSelectedAuthor(data);
      setShowAuthorModal(true);
    } catch (error) {
      console.error('Error fetching author:', error);
      toast.error('Failed to load author details');
    }
  };

  // Fetch author titles
  const fetchAuthorTitles = async (authorId) => {
    try {
      setLoadingTitles(true);
      const response = await fetch(`${API_URL}/api/admin/authors/${authorId}/titles`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch titles');
      
      const data = await response.json();
      setAuthorTitles(data);
      setShowTitlesPanel(true);
      setShowAuthorModal(false);
    } catch (error) {
      console.error('Error fetching titles:', error);
      toast.error('Failed to load titles');
    } finally {
      setLoadingTitles(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAuthors();
    fetchEngagementSummary();
  }, [fetchAuthors, fetchEngagementSummary]);

  // Refresh handler
  const handleRefresh = () => {
    fetchAuthors();
    fetchEngagementSummary();
    toast.success('Data refreshed');
  };

  const totalPages = Math.ceil(totalAuthors / pageSize);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">
                  Author Management Console
                </h1>
                <p className="text-slate-400">
                  Administrative oversight for author accounts, content quality, and performance signals.
                </p>
              </div>
              <Button onClick={handleRefresh} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Context Banner */}
          <ContextBanner />

          {/* Engagement Summary Cards */}
          {engagementSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-slate-900/60 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total Authors</p>
                      <p className="text-2xl font-bold text-white">{formatNumber(engagementSummary.total_authors)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/60 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <BookOpen className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total Titles</p>
                      <p className="text-2xl font-bold text-white">{formatNumber(engagementSummary.total_titles)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/60 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/20">
                      <Clock className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Minutes Consumed</p>
                      <p className="text-2xl font-bold text-white">{formatMinutes(engagementSummary.total_minutes_consumed)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/60 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <CheckCircle2 className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Avg Completion</p>
                      <p className="text-2xl font-bold text-white">{engagementSummary.average_completion_rate}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="bg-slate-900/60 border-slate-700 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search authors..."
                      value={search}
                      onChange={(e) => updateParams({ search: e.target.value, page: '1' })}
                      className="pl-10 bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                
                <Select value={status || "all"} onValueChange={(v) => updateParams({ status: v === "all" ? "" : v, page: '1' })}>
                  <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={days.toString()} onValueChange={(v) => updateParams({ days: v })}>
                  <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Time Period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="365">Last year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Main Content - Authors Table or Titles Panel */}
          {showTitlesPanel ? (
            /* Content Portfolio Panel */
            <Card className="bg-slate-900/60 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Content Portfolio</CardTitle>
                    <CardDescription>Titles by {selectedAuthor?.pen_name || 'Author'}</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => setShowTitlesPanel(false)}>
                    ← Back to Authors
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Non-Financial Disclaimer */}
                <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-amber-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Metrics shown represent Reader engagement and consumption activity. They do not represent earnings, payouts, or revenue share.
                  </p>
                </div>

                {loadingTitles ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : authorTitles.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    No titles found for this author.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Title</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400 text-right">Listens</TableHead>
                        <TableHead className="text-slate-400 text-right">Minutes</TableHead>
                        <TableHead className="text-slate-400 text-right">Completion</TableHead>
                        <TableHead className="text-slate-400 text-right">Readers</TableHead>
                        <TableHead className="text-slate-400">Added</TableHead>
                        <TableHead className="text-slate-400"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {authorTitles.map((title) => (
                        <TableRow key={title.title_id} className="border-slate-700 hover:bg-slate-800/50">
                          <TableCell className="font-medium text-white">{title.title_name}</TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(title.status)}>
                              {title.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-slate-300">{formatNumber(title.total_listens)}</TableCell>
                          <TableCell className="text-right text-slate-300">{formatMinutes(title.total_minutes)}</TableCell>
                          <TableCell className="text-right text-slate-300">{title.completion_rate}%</TableCell>
                          <TableCell className="text-right text-slate-300">{formatNumber(title.unique_readers)}</TableCell>
                          <TableCell className="text-slate-400">{formatDate(title.date_added)}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTitle(title);
                                setShowTitleDrawer(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Author Accounts Panel */
            <Card className="bg-slate-900/60 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Author Accounts</CardTitle>
                <CardDescription>Identity, access, and status management</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : authors.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    No authors found.
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Author</TableHead>
                          <TableHead className="text-slate-400">Titles</TableHead>
                          <TableHead className="text-slate-400">Status</TableHead>
                          <TableHead className="text-slate-400">Tier</TableHead>
                          <TableHead className="text-slate-400">Created</TableHead>
                          <TableHead className="text-slate-400">Last Activity</TableHead>
                          <TableHead className="text-slate-400"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {authors.map((author) => (
                          <TableRow key={author.author_id} className="border-slate-700 hover:bg-slate-800/50">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                                  <UserCog className="w-4 h-4 text-slate-400" />
                                </div>
                                <div>
                                  <p className="font-medium text-white">{author.author_name}</p>
                                  {author.pen_name && author.pen_name !== author.author_name && (
                                    <p className="text-xs text-slate-500">Pen: {author.pen_name}</p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-300">{author.title_count}</TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(author.status)}>
                                {author.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={getTierColor(author.tier)}>
                                {author.tier}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-slate-400">{formatDate(author.created_at)}</TableCell>
                            <TableCell className="text-slate-400">{formatDate(author.last_activity_at)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => fetchAuthorDetail(author.author_id)}
                                  title="View Details"
                                  className="border-slate-600 hover:bg-slate-700 text-slate-200"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => fetchAuthorTitles(author.author_id)}
                                  title="View Titles"
                                  className="border-slate-600 hover:bg-slate-700 text-slate-200"
                                >
                                  <BookOpen className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                        <p className="text-sm text-slate-400">
                          Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalAuthors)} of {totalAuthors}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => updateParams({ page: (page - 1).toString() })}
                          >
                            Previous
                          </Button>
                          <span className="text-sm text-slate-400">
                            Page {page} of {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => updateParams({ page: (page + 1).toString() })}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Top Performers Section */}
          {engagementSummary && !showTitlesPanel && (
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Top Titles by Listens */}
              <Card className="bg-slate-900/60 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    Top Titles by Engagement
                  </CardTitle>
                  <CardDescription>Based on listen count (non-financial)</CardDescription>
                </CardHeader>
                <CardContent>
                  {engagementSummary.top_titles_by_listens?.length > 0 ? (
                    <div className="space-y-3">
                      {engagementSummary.top_titles_by_listens.map((title, idx) => (
                        <div key={title.title_id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-slate-500">#{idx + 1}</span>
                            <div>
                              <p className="font-medium text-white text-sm">{title.title_name}</p>
                              <p className="text-xs text-slate-400">{title.author_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-white">{formatNumber(title.listen_count)}</p>
                            <p className="text-xs text-slate-400">listens</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-4">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Authors by Engagement */}
              <Card className="bg-slate-900/60 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-400" />
                    Top Authors by Engagement
                  </CardTitle>
                  <CardDescription>Based on total consumption (non-financial)</CardDescription>
                </CardHeader>
                <CardContent>
                  {engagementSummary.top_authors_by_engagement?.length > 0 ? (
                    <div className="space-y-3">
                      {engagementSummary.top_authors_by_engagement.map((author, idx) => (
                        <div key={author.author_id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-slate-500">#{idx + 1}</span>
                            <p className="font-medium text-white text-sm">{author.author_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-white">{formatMinutes(author.total_minutes)}</p>
                            <p className="text-xs text-slate-400">consumed</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-4">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      <AuthorDetailModal
        author={selectedAuthor}
        isOpen={showAuthorModal}
        onClose={() => setShowAuthorModal(false)}
        onViewTitles={fetchAuthorTitles}
      />

      <TitleDetailDrawer
        title={selectedTitle}
        isOpen={showTitleDrawer}
        onClose={() => setShowTitleDrawer(false)}
      />

      <AdminMobileBottomNav />
      <UniversalFooter />
    </div>
  );
};

export default AdminAuthorsPage;
